import { useState, useEffect, useRef } from 'react';
import { useAirlinerProfile } from '../../hooks';
import TwoFactorAuthModal from '../../../components/TwoFactorAuthModal/TwoFactorAuthModal';
import ChangePasswordModal from '../../../components/ChangePasswordModal/ChangePasswordModal';
import SearchableSelect from '../../../components/SearchableSelect/SearchableSelect';
import utilityService from '../../../services/utilityService';
import toast from 'react-hot-toast';

const AccountSettings = () => {
  const { 
    profile, 
    twoFactorEnabled, 
    loading, 
    loadProfile, 
    updateProfile, 
    uploadAOCFile,
    uploadLogo,
    changePassword,
    enableTwoFactor,
    disableTwoFactor 
  } = useAirlinerProfile();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '',
    mobileNumber: '',
    timezone: 'Aland Islands (Finland) (+02:00)',
    businessName: '',
    address: '',
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    routingNumber: '',
    aocNumber: '',
    aocFile: null,
    expiryDate: '',
    notApplicable: false
  });

  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showAOCModal, setShowAOCModal] = useState(false);
  const [showUpdateAOCModal, setShowUpdateAOCModal] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false); // Track if user is editing form
  const [updateAOCData, setUpdateAOCData] = useState({
    aocNumber: '',
    aocFile: null,
    expiryDate: '',
    notApplicable: false
  });
  const [pdfUrl, setPdfUrl] = useState('');
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const profileImageInputRef = useRef(null);
  const previewObjectUrlRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [loadingTimezones, setLoadingTimezones] = useState(false);
  const [errors, setErrors] = useState({
    mobileNumber: '',
    businessName: '',
    expiryDate: ''
  });

  // Load profile data on component mount
  useEffect(() => {
    loadProfile();
    fetchCountries();
    fetchTimezones();
  }, [loadProfile]);

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await utilityService.getCountries();
      if (response.success) {
        setCountries(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchTimezones = async () => {
    try {
      setLoadingTimezones(true);
      const response = await utilityService.getTimezones();
      if (response.success) {
        setTimezones(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch timezones:', err);
    } finally {
      setLoadingTimezones(false);
    }
  };

  // Update form data when profile loads
  useEffect(() => {
    if (profile) {
      console.log("profile data is ",profile)
      // Format date for HTML date input (YYYY-MM-DD)
      let formattedDate = '';
      if (profile.expiry_date) {
        try {
          const date = new Date(profile.expiry_date);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split('T')[0];
          }
        } catch (error) {
          console.error('Error formatting date:', error);
        }
      }

      // Only update form data if it's initial load or form is not dirty (user not editing)
      const isInitialLoad = !formData.firstName && !formData.lastName && !formData.email;
      
      if (isInitialLoad || !isFormDirty) {
        setFormData({
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          email: profile.email || '',
          countryCode: profile.country_id || '',
          mobileNumber: profile.mobile_number || '',
          timezone: profile.timezone || 'Aland Islands (Finland) (+02:00)',
          businessName: profile.business_name || '',
          address: profile.address || '',
          bankName: profile.bank_name || '',
          accountNumber: profile.account_number || '',
          accountHolder: profile.account_holder || '',
          routingNumber: profile.routing_number || '',
          aocNumber: profile.aoc_number || '',
          aocFile: profile.aoc, // File uploads are handled separately
          expiryDate: formattedDate,
          notApplicable: profile.not_applicable_expiry === 'Not Applicable'
        });
        
        // Reset dirty flag after initial load
        if (isInitialLoad) {
          setIsFormDirty(false);
        }
      }
    }
  }, [profile, isFormDirty]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Mark form as dirty when user starts editing
    setIsFormDirty(true);
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Store current form data before upload
    const currentFormData = { ...formData };

    setFormData(prev => ({
      ...prev,
      aocFile: file
    }));

    // Upload AOC file immediately
    try {
      setSaving(true);
      await uploadAOCFile(file);
      
      // After successful upload, preserve form data and only update AOC related fields
      setFormData(prev => ({
        ...currentFormData, // Restore all form data
        aocFile: file // Keep the uploaded file
      }));
      
      toast.success('AOC file uploaded successfully!');
    } catch (error) {
      // Restore form data on error
      setFormData(currentFormData);
      toast.error('Failed to upload AOC file. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileImageClick = () => {
    if (profileImageInputRef.current) {
      profileImageInputRef.current.click();
    }
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(file);
      previewObjectUrlRef.current = previewUrl;
      setProfileImagePreview(previewUrl);

      setUploadingProfileImage(true);
      await uploadLogo(file);
      toast.success('Profile image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload profile image. Please try again.');
    } finally {
      setUploadingProfileImage(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Reset errors
    const newErrors = {
      mobileNumber: '',
      businessName: '',
      expiryDate: ''
    };

    // Validate mobile number
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'The Mobile Number field is required.';
    }

    // Validate business name
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'The Business Name field is required.';
    }

    // Validate expiry date (only if not applicable is not checked)
    if (!formData.notApplicable && !formData.expiryDate) {
      newErrors.expiryDate = 'Please check the expiry date / time before completing or saving profile.';
    }

    // Set errors if any
    if (newErrors.mobileNumber || newErrors.businessName || newErrors.expiryDate) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      // Prepare data for API
      const profileData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        mobile_number: formData.mobileNumber,
        business_name: formData.businessName,
        address: formData.address,
        bank_name: formData.bankName,
        account_number: formData.accountNumber,
        account_holder: formData.accountHolder,
        routing_number: formData.routingNumber,
        country_id: formData.countryCode ? Number(formData.countryCode) : null,
        aoc_number: formData.aocNumber,
        expiry_date: formData.notApplicable ? null : formData.expiryDate,
        not_applicable_expiry: formData.notApplicable ? 'Not Applicable' : 'Applicable',
        timezone: formData.timezone
      };

      await updateProfile(profileData);
      
      // Reset dirty flag after successful save
      setIsFormDirty(false);
      
      toast.success('Account settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save account settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original profile data
    if (profile) {
      // Format date for HTML date input (YYYY-MM-DD)
      let formattedDate = '';
      if (profile.expiry_date) {
        try {
          const date = new Date(profile.expiry_date);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split('T')[0];
          }
        } catch (error) {
          console.error('Error formatting date:', error);
        }
      }

      setFormData({
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        email: profile.email || '',
        countryCode: profile.country_id || '',
        mobileNumber: profile.mobile_number || '',
        timezone: profile.timezone || 'Aland Islands (Finland) (+02:00)',
        businessName: profile.business_name || '',
        address: profile.address || '',
        bankName: profile.bank_name || '',
        accountNumber: profile.account_number || '',
        accountHolder: profile.account_holder || '',
        routingNumber: profile.routing_number || '',
        aocNumber: profile.aoc_number || '',
        aocFile: profile.aoc,
        expiryDate: formattedDate,
        notApplicable: profile.not_applicable_expiry === 'Not Applicable'
      });
    }
    
    // Clear any errors
    setErrors({
      mobileNumber: '',
      businessName: '',
      expiryDate: ''
    });
    
    // Reset dirty flag
    setIsFormDirty(false);
  };

  const handle2FAToggle = async (e) => {
    const isEnabled = e.target.checked;
    if (isEnabled) {
      // Toggle is turned ON - show modal
      setShow2FAModal(true);
    } else {
      // Toggle is turned OFF - directly disable 2FA
      try {
        await disableTwoFactor();
        toast.success('2FA Authentication disabled successfully!');
      } catch (error) {
        toast.error('Failed to disable 2FA. Please try again.');
      }
    }
  };

  const handle2FASave = async (data) => {
    try {
      await enableTwoFactor(data);
      setShow2FAModal(false);
      toast.success('2FA Authentication enabled successfully!');
    } catch (error) {
      toast.error('Failed to enable 2FA. Please try again.');
    }
  };

  const handle2FAClose = () => {
    setShow2FAModal(false);
  };

  const handleChangePassword = () => {
    setShowChangePasswordModal(true);
  };

  const handlePasswordSave = async (passwordData) => {
    try {
      await changePassword(passwordData);
      setShowChangePasswordModal(false);
      toast.success('Password changed successfully!');
    } catch (error) {
      toast.error('Failed to change password. Please try again.');
    }
  };

  const handlePasswordClose = () => {
    setShowChangePasswordModal(false);
  };

  const calculateRemainingTime = () => {
    if (!profile?.expiry_date || profile?.not_applicable_expiry === 'Not Applicable') {
      return null;
    }

    const expiryDate = new Date(profile.expiry_date);
    const today = new Date();
    const diffTime = expiryDate - today;
    
    if (diffTime <= 0) {
      return { days: 0, hours: 0, minutes: 0, expired: true };
    }

    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

    return { days, hours, minutes, expired: false };
  };

  const handleOpenUpdateAOCModal = () => {
    setUpdateAOCData({
      aocNumber: profile?.aoc_number || '',
      aocFile: null,
      expiryDate: profile?.expiry_date ? new Date(profile.expiry_date).toISOString().split('T')[0] : '',
      notApplicable: profile?.not_applicable_expiry === 'Not Applicable'
    });
    setShowUpdateAOCModal(true);
  };

  const handleOpenAOCModal = async () => {
    setLoadingPdf(true);
    try {
      // Get signed URL from backend
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
      const token = localStorage.getItem('token'); // Assuming token is stored in localStorage
      
      const response = await fetch(`${apiUrl}/auth/profile/aoc/signed-url`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get signed URL');
      }

      const data = await response.json();
      setPdfUrl(data.data.aoc_url);
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Failed to load PDF');
    } finally {
      setLoadingPdf(false);
    }
    setShowAOCModal(true);
  };

  const handleCloseAOCModal = () => {
    setShowAOCModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
  };

  const handleUpdateAOCChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUpdateAOCData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateAOCFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUpdateAOCData(prev => ({
        ...prev,
        aocFile: file
      }));
    }
  };

  const handleUpdateAOCSave = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Store current form data before update
      const currentFormData = { ...formData };
      
      // Upload file first if provided
      if (updateAOCData.aocFile) {
        await uploadAOCFile(updateAOCData.aocFile);
      }

      // Prepare profile data with updated AOC info
      const profileData = {
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || '',
        email: profile?.email || '',
        mobile_number: profile?.mobile_number || '',
        business_name: profile?.business_name || '',
        address: profile?.address || '',
        bank_name: profile?.bank_name || '',
        account_number: profile?.account_number || '',
        account_holder: profile?.account_holder || '',
        routing_number: profile?.routing_number || '',
        timezone: profile?.timezone || '',
        country_id: profile?.country_id || null,
        aoc_number: updateAOCData.aocNumber,
        expiry_date: updateAOCData.notApplicable ? null : updateAOCData.expiryDate,
        not_applicable_expiry: updateAOCData.notApplicable ? 'Not Applicable' : 'Applicable'
      };

      await updateProfile(profileData);
      
      // Restore form data and update only AOC related fields
      setFormData(prev => ({
        ...currentFormData,
        aocNumber: updateAOCData.aocNumber,
        expiryDate: updateAOCData.expiryDate,
        notApplicable: updateAOCData.notApplicable
      }));
      
      toast.success('AOC document updated successfully!');
      setShowUpdateAOCModal(false);
    } catch (error) {
      toast.error('Failed to update AOC document. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseUpdateAOCModal = () => {
    setShowUpdateAOCModal(false);
  };

  // Show loading state
  if (loading && !profile) {
    return (
      <div className="p-0 ml-2 bg-black min-h-screen w-[100%] relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }


  const displayedProfileImage = profileImagePreview || profile?.image_url;

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
      
      <div className="p-0 ml-0 lg:ml-6 bg-white min-h-screen relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif] flex flex-col overflow-hidden">
      <div className="flex-1">
      <div className="p-[15px] md:p-[15px_20px] pr-[20px] max-w-full m-0 bg-jet-gray-100">
        <h1 className="text-[20px] font-semibold text-[#333] mb-[20px] md:mb-[30px] text-left hidden md:block">Account Settings</h1>

        <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-5 md:gap-10">
          {/* Left Column - Profile */}
          <div className="lg:sticky lg:top-[20px] lg:self-start h-fit">
            <div className="bg-white rounded-lg p-[20px] md:p-[25px_20px] text-center shadow-none">
              <div className="mb-[15px]">
                <div
                  className="w-[100px] h-[100px] rounded-full bg-[#ccc] mx-auto mb-2 flex items-center justify-center relative border-[3px] border-[#e0e0e0] cursor-pointer group"
                  onClick={!uploadingProfileImage ? handleProfileImageClick : undefined}
                >
                  {displayedProfileImage ? (
                    <img
                      src={displayedProfileImage}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  )}
                  {/* Hover tooltip */}
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-[#222] text-white text-[11px] whitespace-nowrap px-3 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                    Click to change profile image.
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#222]"></span>
                  </div>
                  <input
                    ref={profileImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    className="hidden"
                  />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-[#333] my-[10px_0_5px]">{profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User' : 'Loading...'}</h3>
              <p className="text-[13px] text-[#777] m-[0_0_15px]">{profile?.user_type || 'Airliner'}</p>
              <button 
                className="inline-block mb-0 text-center whitespace-nowrap align-middle cursor-pointer border border-transparent px-3 py-1.5 text-[14px] leading-[1.42857143] rounded shadow-none bg-gradient-to-br from-[#ffeb00] to-[#ffd200] text-black block w-full mb-[15px] hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] transition-all duration-200" 
                onClick={handleChangePassword}
              >
                Change Password
              </button>
              <div className="flex justify-between items-center py-3 border-t border-[#e0e0e0] text-[13px] text-[#555]">
                <span>2FA Authentication</span>
                <label className="relative inline-block w-[60px] h-[30px] shrink-0 align-middle bg-[#d1d3d1] rounded-[30px] border border-[#bbb] cursor-pointer has-[:checked]:bg-[#ffeb00] has-[:checked]:border-[#ffeb00]">
                  <input
                    type="checkbox"
                    checked={twoFactorEnabled}
                    onChange={handle2FAToggle}
                    className="hidden peer"
                  />
                  <span className="absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-transparent transition-all duration-[400ms] ease-[ease] rounded-[30px] before:absolute before:content-[''] before:h-6 before:w-6 before:left-[3px] before:top-[3px] before:bg-white before:transition-all before:duration-[400ms] before:ease-[ease] before:rounded-full before:shadow-[0_2px_4px_rgba(0,0,0,0.2)] peer-checked:before:translate-x-[30px]"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column - Forms */}
          <div className="rounded-none shadow-none">
            <form onSubmit={handleSave}>
              {/* Personal Details */}
              <div className="rounded-none p-2.5 shadow-none bg-white">
                <h2 className="inline-block text-base font-semibold m-0 leading-none text-[#333] mb-5 pb-0 border-b-0">Personal Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">First Name <span className="text-red-600">*</span></label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="DEMO"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                      required
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">Last Name <span className="text-red-600">*</span></label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="USER"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_0.5fr_0.5fr] gap-5 col-span-1 md:col-span-2">
                    <div className="flex flex-col">
                      <label className="inline-block max-w-full text-[13px] mb-[5px]">Email <span className="text-red-600">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="demouser@mailinator.com"
                        className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                        required
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="inline-block max-w-full text-[13px] mb-[5px]">Country Code <span className="text-red-600">*</span></label>
                      <SearchableSelect
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        options={countries.map((country) => ({
                          value: country.id,
                          label: `${country.name} ${country.phone_code}`
                        }))}
                        placeholder="Select Country"
                        disabled={loadingCountries}
                        className="w-full"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="inline-block max-w-full text-[13px] mb-[5px]">Mobile Number <span className="text-red-600">*</span></label>
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        placeholder="Mobile Number"
                        className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                      />
                      {errors.mobileNumber && (
                        <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors.mobileNumber}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">Timezone</label>
                    <SearchableSelect
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      options={timezones.map((tz) => ({
                        value: `${tz.location} (${tz.timezone})`,
                        label: `${tz.location} (${tz.timezone})`
                      }))}
                      placeholder="Select Timezone"
                      disabled={loadingTimezones}
                      className="w-full"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">Business Name <span className="text-red-600">*</span></label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      placeholder="BUSINESS NAME"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                    />
                    {errors.businessName && (
                      <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors.businessName}</p>
                    )}
                  </div>
                  <div className="flex flex-col col-span-1 md:col-span-2">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="ADDRESS"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 h-auto focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                      rows="3"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="rounded-none p-2.5 shadow-none bg-white mt-2.5">
                <h2 className="inline-block text-base font-semibold m-0 leading-none text-[#333] mb-5 pb-0 border-b-0">Bank Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">Bank Name</label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleChange}
                      placeholder="BANK NAME"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">Account Number</label>
                    <input
                      type="text"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleChange}
                      placeholder="Account Number"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">Account Holder</label>
                    <input
                      type="text"
                      name="accountHolder"
                      value={formData.accountHolder}
                      onChange={handleChange}
                      placeholder="ACCOUNT HOLDER"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="inline-block max-w-full text-[13px] mb-[5px]">Routing Number</label>
                    <input
                      type="text"
                      name="routingNumber"
                      value={formData.routingNumber}
                      onChange={handleChange}
                      placeholder="ROUTING NUMBER"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                    />
                  </div>
                </div>
              </div>

              {/* Air Operator Certificate */}
              <div className="rounded-none p-2.5 shadow-none bg-white mt-2.5">
                <h2 className="inline-block text-base font-semibold m-0 leading-none text-[#333] mb-5 pb-0 border-b-0">Air Operator Certificate (AOC)</h2>
                
                {profile?.aoc && (
                  <div className="space-y-4">
                    {/* AOC Number */}
                    <div className="flex flex-col max-w-[450px]">
                      <label className="inline-block max-w-full text-[13px] mb-[5px] text-[#555]">AOC Number</label>
                      <input
                        type="text"
                        value={profile.aoc_number || ''}
                        readOnly
                        className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 cursor-not-allowed"
                      />
                    </div>

                    {/* AOC PDF and Update AOC buttons */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleOpenAOCModal}
                        className="bg-[#00bcd4] text-white px-5 py-2 rounded text-[13px] hover:bg-[#0097a7] transition-colors h-[38px]"
                      >
                        AOC PDF
                      </button>
                      <button
                        type="button"
                        className="bg-[#ffeb00] text-black px-5 py-2 rounded text-[13px] hover:bg-[#ffd200] transition-colors h-[38px]"
                        onClick={handleOpenUpdateAOCModal}
                      >
                        Update AOC
                      </button>
                    </div>

                    {/* Expiry Date */}
                    <div className="flex flex-col">
                      <label className="inline-block max-w-full text-[13px] mb-[5px] text-[#555]">Expiry Date</label>
                      <div className="flex gap-3 items-center">
                        <div className="relative max-w-[300px]">
                          <input
                            type="date"
                            value={profile.expiry_date ? new Date(profile.expiry_date).toISOString().split('T')[0] : ''}
                            readOnly
                            className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 pr-8 leading-[1.42857143] bg-[#eee] opacity-100 cursor-not-allowed"
                          />
                        </div>
                        <label className="flex items-center gap-2 text-[13px] text-[#555] cursor-pointer font-normal whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={profile.not_applicable_expiry === 'Not Applicable'}
                            readOnly
                            disabled
                            className="w-4 h-4 cursor-not-allowed accent-[#ffeb00]"
                          />
                          Not Applicable
                        </label>
                      </div>
                      {calculateRemainingTime() && !calculateRemainingTime().expired && (
                        <p className="text-[11px] text-[#666] mt-2 mb-0">
                          Time remaining: {calculateRemainingTime().days} days, {calculateRemainingTime().hours} hours, {calculateRemainingTime().minutes} minutes
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {!profile?.aoc && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="flex flex-col col-span-1 md:col-span-2">
                      <label className="inline-block max-w-full text-[13px] mb-[5px]">AOC Number</label>
                      <input
                        type="text"
                        name="aocNumber"
                        value={formData.aocNumber}
                        onChange={handleChange}
                        placeholder="AOC NUMBER"
                        className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 focus:outline-none focus:border-[#e0e0e0] focus:bg-[#f5f5f5] transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col col-span-1 md:col-span-2">
                      <label className="inline-block max-w-full text-[13px] mb-[5px]">AOC File</label>
                      <input
                        type="file"
                        accept='.pdf'
                        name="aocFile"
                        onChange={handleFileChange}
                        className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-[#eee] opacity-100 p-2"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="inline-block max-w-full text-[13px] mb-[5px]">Expiry Date</label>
                      <div className="flex flex-col gap-2.5">
                          <input
                            type="date"
                            name="expiryDate"
                            value={formData.expiryDate}
                            onChange={handleChange}
                            readOnly={formData.notApplicable}
                          className={`appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[12px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] focus:outline-none focus:border-[#e0e0e0] transition-colors duration-200 ${formData.notApplicable ? 'bg-[#e9ecef] cursor-not-allowed opacity-60' : 'bg-[#eee] opacity-100 focus:bg-[#f5f5f5]'}`}
                            onClick={(e) => formData.notApplicable && e.preventDefault()}
                          />
                        {errors.expiryDate && (
                          <p className="text-[#ff6b6b] text-[12px] mt-0 mb-0">{errors.expiryDate}</p>
                        )}
                        <label className="flex items-center gap-2 text-[13px] text-[#666] cursor-pointer font-normal">
                          <input
                            type="checkbox"
                            name="notApplicable"
                            checked={formData.notApplicable}
                            onChange={handleChange}
                            className="w-auto h-auto m-0 cursor-pointer"
                          />
                          Not Applicable
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-[15px] mt-2.5 border-t-0 bg-white p-2.5">
                <button 
                  type="submit" 
                  disabled={saving}
                  className="inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer border border-transparent py-2.5 px-4 text-[14px] leading-[16.8px] rounded shadow-none bg-gradient-to-br from-[#ffeb00] to-[#ffd200] text-black h-9 w-full sm:w-24 font-['Bahnschrift',sans-serif]  hover:bg-[#be7405] hover:border-[rgba(0,0,0,0.2)] focus:text-[#333] focus:no-underline select-none disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button 
                  type="button" 
                  className="inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer border border-[#ddd] py-2.5 px-4 text-[14px] leading-[16.8px] rounded shadow-none bg-[#f4f4f4] text-[#444] h-9 w-full sm:w-24 font-['Bahnschrift',sans-serif] hover:text-[#333] hover:no-underline focus:text-[#333] focus:no-underline select-none" 
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      </div>

      {/* Copyright Section */}
      <div className="pt-0 p-[15px] mr-auto ml-auto px-[15px] bg-jet-gray-100 w-full">
        <div className="bg-white p-[10px] md:p-[15px] text-black border-t-0 text-center shadow-none pb-[10px] rounded-[5px] text-[12px] md:text-[14px]">
          Copyright © 2026 Jetscanner360. All rights reserved.
        </div>
      </div>

      {/* 2FA Authentication Modal */}
      <TwoFactorAuthModal
        isOpen={show2FAModal}
        onClose={handle2FAClose}
        onSave={handle2FASave}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={handlePasswordClose}
        onSave={handlePasswordSave}
      />

      {/* AOC Document Modal */}
      {showAOCModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 animate-fadeIn pt-16">
          <div className="bg-white rounded-[8px] w-[900px] h-[544px] max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-[#e0e0e0]">
              <h4 className="text-[20px] font-normal text-[#666] m-0">AOC Document</h4>
              <button
                type="button"
                onClick={handleCloseAOCModal}
                className="text-[#ccc] hover:text-[#999] text-[28px] leading-none font-normal cursor-pointer transition-colors"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-hidden bg-[#525659]">
              {loadingPdf ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00bcd4] mx-auto mb-4"></div>
                    <p className="text-white">Loading PDF...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <embed
                  src={pdfUrl}
                  type="application/pdf"
                  className="w-full"
                  style={{ height: '500px' }}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white">Unable to load PDF</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#e0e0e0] bg-white">
              <button
                type="button"
                onClick={handleCloseAOCModal}
                className="px-6 py-2 bg-white text-[#555] border border-[#ddd] rounded-[4px] hover:bg-[#f9f9f9] transition-colors text-[15px] font-normal h-[42px] min-w-[100px]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update AOC Document Modal */}
      {showUpdateAOCModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-20 animate-fadeIn">
          <div className="bg-white rounded-[8px] w-[600px] max-w-[95vw] animate-slideDown shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b-0">
              <h4 className="text-[20px] font-normal text-[#666] m-0">Air Operator Certificate (AOC)</h4>
              <button
                type="button"
                onClick={handleCloseUpdateAOCModal}
                className="text-[#ccc] hover:text-[#999] text-[28px] leading-none font-normal cursor-pointer transition-colors"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleUpdateAOCSave}>
              <div className="px-6 pb-4 pt-2 space-y-4">
                {/* AOC Number */}
                <div>
                  <label className="block text-[14px] text-[#555] mb-2">AOC Number</label>
                  <input
                    type="text"
                    name="aocNumber"
                    value={updateAOCData.aocNumber}
                    onChange={handleUpdateAOCChange}
                    placeholder=""
                    className="w-full px-4 py-2.5 border border-[#ddd] rounded-[4px] text-[14px] font-normal text-[#555] bg-white h-[42px] focus:outline-none focus:border-[#ccc] transition-colors"
                  />
                </div>

                {/* AOC File */}
                <div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleUpdateAOCFileChange}
                    className="w-full px-4 py-2.5 border border-[#ddd] rounded-[4px] text-[14px] text-[#666] bg-white h-[42px] focus:outline-none file:mr-4 file:py-1.5 file:px-4 file:rounded file:border-0 file:text-[13px] file:font-normal file:bg-[#f5f5f5] file:text-[#555] hover:file:bg-[#e9e9e9] file:cursor-pointer"
                  />
                  {updateAOCData.aocFile && (
                    <p className="text-[12px] text-[#999] mt-2">Selected: {updateAOCData.aocFile.name}</p>
                  )}
                </div>

                {/* Expiry Date */}
                <div>
                  <label className="block text-[14px] text-[#555] mb-2">Expiry Date</label>
                  <div className="flex gap-4 items-center">
                    <input
                      type="date"
                      name="expiryDate"
                      value={updateAOCData.expiryDate}
                      onChange={handleUpdateAOCChange}
                      readOnly={updateAOCData.notApplicable}
                      className={`flex-1 max-w-[280px] px-4 py-2.5 border border-[#ddd] rounded-[4px] text-[14px] font-normal text-[#555] h-[42px] focus:outline-none focus:border-[#ccc] transition-colors ${updateAOCData.notApplicable ? 'bg-[#f5f5f5] cursor-not-allowed opacity-60' : 'bg-white'}`}
                      onClick={(e) => updateAOCData.notApplicable && e.preventDefault()}
                    />
                    <label className="flex items-center gap-2 text-[14px] text-[#555] cursor-pointer whitespace-nowrap font-normal">
                      <input
                        type="checkbox"
                        name="notApplicable"
                        checked={updateAOCData.notApplicable}
                        onChange={handleUpdateAOCChange}
                        className="w-[18px] h-[18px] cursor-pointer accent-[#ffeb00]"
                      />
                      Not Applicable
                    </label>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-between px-6 py-4 border-t-0 bg-white">
                <button
                  type="button"
                  onClick={handleCloseUpdateAOCModal}
                  className="px-8 py-2.5 bg-white text-[#555] border border-[#ddd] rounded-[4px] hover:bg-[#f9f9f9] transition-colors text-[15px] font-normal h-[42px] min-w-[120px]"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-8 py-2.5 bg-[#ffeb00] text-[#333] border-0 rounded-[4px] hover:bg-[#ffd200] transition-colors text-[15px] font-semibold h-[42px] min-w-[120px] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AccountSettings;
